import type { RubyPlatformAdapter } from "@attmos/ruby-ui";
import type {
  ChatRequestParams,
  ChatResponse,
  StreamFrame,
  ChatSession,
  ChatMessage,
  LlamaStatus,
} from "@attmos/ruby-core";
import { parseSSEStream } from "@attmos/ruby-core";

import { apiFetch, resolveApiUrl } from "@/api/client";
import type { Session } from "@/api/auth";

export function createFlowStackAdapter(session: Session | null): RubyPlatformAdapter {
  return {
    platformId: "flowstack",
    storageKeyPrefix: "flowstack",

    getUserId: () => session?.user_id ?? null,

    async sendMessage(params: ChatRequestParams): Promise<ChatResponse> {
      const { signal, ...body } = params;
      return apiFetch<ChatResponse>("/api/chat", {
        method: "POST",
        json: body,
        signal,
      });
    },

    async *streamMessage(params: ChatRequestParams): AsyncIterable<StreamFrame> {
      const { signal, ...body } = params;
      const url = await resolveApiUrl("/api/chat/stream");
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(`POST /api/chat/stream failed (${res.status}): ${detail}`);
      }

      const ct = res.headers.get("content-type") ?? "";
      if (!ct.toLowerCase().startsWith("text/event-stream")) {
        throw new Error(
          `POST /api/chat/stream returned ${res.status} ${ct || "(no content-type)"}`
        );
      }

      if (!res.body) {
        throw new Error("POST /api/chat/stream returned an empty body");
      }

      yield* parseSSEStream(res.body);
    },

    async listSessions(): Promise<ChatSession[]> {
      return apiFetch<ChatSession[]>("/api/chat/sessions");
    },

    async getSessionMessages(sessionId: string): Promise<ChatMessage[]> {
      return apiFetch<ChatMessage[]>(
        `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`
      );
    },

    async deleteSession(sessionId: string): Promise<void> {
      return apiFetch<void>(`/api/chat/sessions/${encodeURIComponent(sessionId)}`, {
        method: "DELETE",
      });
    },

    async renameSession(sessionId: string, title: string): Promise<void> {
      return apiFetch<void>(`/api/chat/sessions/${encodeURIComponent(sessionId)}`, {
        method: "PATCH",
        json: { title },
      });
    },

    async sendFeedback(
      sessionId: string,
      messageId: string,
      rating: "up" | "down" | null
    ): Promise<void> {
      return apiFetch<void>(
        `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
        { method: "POST", json: { rating } }
      );
    },

    async checkHealth(): Promise<LlamaStatus> {
      try {
        const data = await apiFetch<{ status?: LlamaStatus }>("/api/chat/health");
        return data.status ?? "unknown";
      } catch {
        return "down";
      }
    },

    features: {
      streaming: true,
      receiptUpload: false,
      financialContext: false,
      thinkingMode: true,
      sessionPinning: true,
      contextPill: false,
    },

    greeting: `Hi${session?.display_name ? ` ${session.display_name.split(" ")[0]}` : ""}! I'm Ruby. I can help with FlowStack workflows, simulation setup, computational chemistry questions, or anything in the docs. What's up?`,

    starterChips: [
      {
        label: "What can you help with?",
        prompt: "What can you help me with?",
      },
      {
        label: "Explain this workflow",
        prompt: "Explain how the current workflow is structured",
      },
      {
        label: "Amber simulation basics",
        prompt: "How do I set up a basic Amber molecular dynamics simulation?",
      },
    ],

    chatModels: [
      {
        id: "qwen3.6-35b-a3b-nvfp4",
        shortName: "Qwen 3.6 35B",
        description: "Qwen 3.6 35B with thinking mode",
      },
    ],
    defaultModelId: "qwen3.6-35b-a3b-nvfp4",
  };
}
